'use strict'

import {useCallback} from 'react'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {getSiteEnvVarValue} from './site-config'
import {toBrandCode, useBrand} from '../contexts/brand-context'
import {
    UGC_FLAG_NAMES,
    UGC_LIMIT_NAMES,
    UGC_DEFAULT_PAGE_SIZE,
    UGC_PLACEMENT,
    FOR_YOU_CATEGORY_ATTRIBUTE
} from '../constants'

/**
 * Builds the brand+flag-scoped env var name as it is defined in config, e.g.
 * buildScopedEnvVarName('DYN', 'BV_UGC_ENABLED') -> 'DYN_BV_UGC_ENABLED'.
 *
 * Single source of truth for this naming convention — both getUgcFlag and
 * getUgcLimit must build the exact same name, or they'll silently look up an
 * env var that doesn't exist. Also reused by the "For You" landing page flags so
 * the convention can't drift. The brandCode is the upper-case short code from
 * toBrandCode ('GRG' | 'DYN') and the base names are upper snake case, matching
 * the keys exported by config.js.
 *
 * @param {string} brandCode - result of toBrandCode(brandId), e.g. 'DYN'
 * @param {string} name - unprefixed base name, e.g. 'BV_UGC_ENABLED'
 * @returns {string}
 */
export const buildScopedEnvVarName = (brandCode, name) => {
    // Empty name makes getSiteEnvVarValue return undefined, so a missing brand
    // or base name reads as "not configured" instead of throwing.
    if (typeof brandCode !== 'string' || !brandCode || !name) return ''
    return `${brandCode.toUpperCase()}_${name}`
}

// Positive-integer env var value, or `fallback` when unset/invalid/zero/negative.
const parsePositiveInt = (raw, fallback) => {
    const parsed = parseInt(raw, 10)
    return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback
}

/**
 * Resolves a UGC boolean flag scoped to BOTH brand and country/site.
 *
 * Site env vars in this codebase are already scoped per-siteId (which
 * encodes country — see SITE_COUNTRY_MAP), but a single site can serve BOTH
 * brands (e.g. 'DynamiteGarageCA'), so siteId alone can't tell Dynamite and
 * Garage apart. The fix: brand-prefix the ENV VAR NAME itself
 * (e.g. 'DYN_BV_UGC_LANDING_ENABLED' vs 'GRG_BV_UGC_LANDING_ENABLED'), then still
 * resolve that name per-siteId for country. This combination is what gives
 * true per-brand + per-country granularity using existing infrastructure.
 *
 * @param {Object} appConfig - from getConfig().app
 * @param {string} flagName - one of UGC_FLAG_NAMES values (unprefixed base name)
 * @param {string} siteId
 * @param {string} brandId - 'dynamite' | 'garage' (whatever useBrand().brandId returns)
 * @returns {boolean}
 */
export const getUgcFlag = (appConfig, flagName, siteId, brandId) => {
    const brandCode = toBrandCode(brandId)
    if (!brandCode) return false

    const scopedName = buildScopedEnvVarName(brandCode, flagName)
    return getSiteEnvVarValue(appConfig, scopedName, siteId) === 'true'
}

/**
 * Resolves a UGC numeric limit (page size) scoped to brand + site, with a
 * fallback to UGC_DEFAULT_PAGE_SIZE when unconfigured or invalid.
 *
 * @param {Object} appConfig
 * @param {string} limitName - one of UGC_LIMIT_NAMES values
 * @param {string} siteId
 * @param {string} brandId
 * @param {number} [fallback=UGC_DEFAULT_PAGE_SIZE]
 * @returns {number}
 */
export const getUgcLimit = (
    appConfig,
    limitName,
    siteId,
    brandId,
    fallback = UGC_DEFAULT_PAGE_SIZE
) => {
    const brandCode = toBrandCode(brandId)
    if (!brandCode) return fallback

    const scopedName = buildScopedEnvVarName(brandCode, limitName)
    return parsePositiveInt(getSiteEnvVarValue(appConfig, scopedName, siteId), fallback)
}

/**
 * Home Page UGC item limit. Brand-agnostic (the per-placement datasource
 * mapping is ECOM-managed), so it is read straight from the app config with no
 * brand prefix — never through getUgcLimit.
 * Falls back to UGC_DEFAULT_PAGE_SIZE when unset/invalid.
 *
 * @param {Object} appConfig
 * @param {number} [fallback=UGC_DEFAULT_PAGE_SIZE]
 * @returns {number}
 */
export const getUgcHomePageLimit = (appConfig, fallback = UGC_DEFAULT_PAGE_SIZE) => {
    return parsePositiveInt(getSiteEnvVarValue(appConfig, UGC_LIMIT_NAMES.HOMEPAGE), fallback)
}

/**
 * Convenience: is UGC enabled at all for this brand+site, for a given
 * placement? Always requires the master flag.
 *
 * Landing/PDP also require their brand-scoped placement flag. The Home Page
 * carousel has no dedicated flag — CMS authoring (the UGC dynamic source) plus
 * the ECOM-managed datasource mapping control whether it appears, so only the
 * master flag applies.
 *
 * @param {Object} appConfig
 * @param {'homepage'|'landing'|'pdp'} placement
 * @param {string} siteId
 * @param {string} brandId
 * @returns {boolean}
 */
export const isUgcPlacementEnabled = (appConfig, placement, siteId, brandId) => {
    const bvUgcEnabled = getUgcFlag(appConfig, UGC_FLAG_NAMES.BV_UGC_ENABLED, siteId, brandId)
    if (!bvUgcEnabled) return false

    if (placement === UGC_PLACEMENT.HOMEPAGE) return true

    // An unknown or missing placement must not fall through to the PDP flag.
    const placementFlagName = {
        [UGC_PLACEMENT.LANDING]: UGC_FLAG_NAMES.LANDING_ENABLED,
        [UGC_PLACEMENT.PDP]: UGC_FLAG_NAMES.PDP_ENABLED
    }[placement]
    if (!placementFlagName) return false

    return getUgcFlag(appConfig, placementFlagName, siteId, brandId)
}

/**
 * Total-count flag. Unlike the placement flags this one is NOT brand-scoped —
 * config exposes it as the single global key BV_UGC_TOTAL_COUNT_ENABLED — so it
 * is read straight from the app config with no brand prefix and no siteId. The
 * feature is Dynamite-only, so Garage always reports false regardless of the
 * flag value.
 *
 * @param {Object} appConfig
 * @param {string} brandId
 * @returns {boolean}
 */
export const isUgcTotalCountEnabled = (appConfig, brandId) => {
    if (brandId !== 'dynamite') return false

    return getSiteEnvVarValue(appConfig, UGC_FLAG_NAMES.TOTAL_COUNT_ENABLED) === 'true'
}

/**
 * Checks if a category is explicitly marked as a UGC landing category.
 * Used to keep UGC categories in navigation even without orderable products.
 *
 * @param {Object} category - Category or navigation node
 * @returns {boolean} True only when `c_isUGC` is exactly true
 */
export const isUgcCategory = (category) => category?.c_isUGC === true

/**
 * Finds the first category flagged as a UGC landing category (`c_isUGC === true`).
 *
 * @param {Object} node - Category node holding an `itemsKey` array of children.
 * @param {string} [itemsKey='categories'] - Property holding the child array.
 * @returns {Object|null} The UGC category node, or null when none is marked.
 */
export const findUgcCategory = (node, itemsKey = 'categories') => {
    if (!node) return null
    if (isUgcCategory(node)) return node

    const children = Array.isArray(node[itemsKey]) ? node[itemsKey] : []
    for (const child of children) {
        const found = findUgcCategory(child, itemsKey)
        if (found) return found
    }

    return null
}

/**
 * Recursively filters a category tree so that a category marked isUGC=true is
 * removed when the UGC master flag is off for the current brand+site.
 * SIT also returns c_isUGC=false for ordinary categories, so false cannot be
 * used to identify a disabled UGC category without hiding the whole menu.
 *
 * Pure/synchronous — no hooks, no network calls. Safe to call directly on a
 * category tree that was already fetched and passed down as a prop, which is
 * exactly the case for ListMenu's `root` and DrawerMenu's `category` — neither
 * goes through useActiveCategoriesTree's async filtering.
 *
 * @param {Object} node - category object with a nested children array
 * @param {string} itemsKey - property holding the children array (e.g. 'categories')
 * @param {boolean} isUgcMasterEnabled - result of getUgcFlag(..., UGC_FLAG_NAMES.BV_UGC_ENABLED, ...)
 * @returns {Object} node, unchanged if no children, otherwise a shallow clone with filtered children
 */
export const filterUgcCategoryTree = (node, itemsKey, isUgcMasterEnabled) => {
    if (!node || !Array.isArray(node[itemsKey]) || node[itemsKey].length === 0) {
        return node
    }
    return {
        ...node,
        [itemsKey]: node[itemsKey]
            .filter((child) => {
                if (!child) return false
                if (child.c_isUGC === true && !isUgcMasterEnabled) return false
                return true
            })
            .map((child) => filterUgcCategoryTree(child, itemsKey, isUgcMasterEnabled))
    }
}

/**
 * Hook wrapper around filterUgcCategoryTree — resolves the master UGC flag
 * for the current brand+site once, and returns a stable filter function.
 * Use this in any nav component that receives an already-fetched category
 * tree as a prop and needs to apply the UGC master flag before rendering.
 *
 * @param {string} [itemsKey='categories']
 * @returns {(node: Object) => Object} filter function
 */
export const useUgcCategoryFilter = (itemsKey = 'categories') => {
    const {site} = useMultiSite() || {}
    const {brandId} = useBrand() || {}
    const {app: appConfig} = getConfig()
    const isUgcMasterEnabled = getUgcFlag(
        appConfig,
        UGC_FLAG_NAMES.BV_UGC_ENABLED,
        site?.id,
        brandId
    )

    return useCallback(
        (node) => filterUgcCategoryTree(node, itemsKey, isUgcMasterEnabled),
        [itemsKey, isUgcMasterEnabled]
    )
}
